'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import { sectionHeadingTag } from '@/components/dynamic-zone/section-heading-level';
import { components } from '@/types/strapi';
import { SectionContainer } from '../../ui/SectionContainer';

export default function LeadershipSection({
	heading,
	sub_heading,
	leadership_content,
	isFirst = false,
}: components['schemas']['DynamicZoneLeadershipComponent'] & {
	isFirst?: boolean;
}) {
	const SectionHeading = sectionHeadingTag(isFirst);
	return (
		<SectionContainer className="leadership-section relative mt-16">
			<noscript>
				<style>{`
					.leadership-section h2[style],
					.leadership-section h3[style],
					.leadership-section p[style],
					.leadership-section div[style*='opacity'],
					.leadership-section div[style*='scale'],
					.leadership-section div[style*='translateX'],
					.leadership-section div[style*='translateY'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			<div className="mx-auto max-w-7xl">
				<motion.div
					className="mb-16 text-center"
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.6 }}>
					<SectionHeading className="mb-4 text-4xl font-bold md:text-5xl">
						{heading}
					</SectionHeading>
					<div className="mx-auto mb-6 h-1 w-20 rounded-full bg-primary"></div>
					<p className="mx-auto max-w-3xl text-xl text-gray-600">
						{sub_heading}
					</p>
				</motion.div>

				<div className="mx-auto grid max-w-6xl gap-12 md:grid-cols-2">
					{leadership_content?.map((leader, index) => (
						<LeaderCard key={index} leader={leader} index={index} />
					))}
				</div>
			</div>
		</SectionContainer>
	);
}

interface LeaderCardProps {
	leader: components['schemas']['ItemsLeadershipItemComponent'];
	index: number;
	badges?: components['schemas']['ItemsBadgeItemComponent'];
}

const LeaderCard = ({ leader, index }: LeaderCardProps) => {
	return (
		<motion.div
			initial={{ opacity: 0, x: index === 0 ? -20 : 20 }}
			whileInView={{ opacity: 1, x: 0 }}
			viewport={{ once: true }}
			transition={{ duration: 0.5 }}
			className="group h-full">
			<div className="relative flex h-full flex-col rounded-2xl bg-white/80 p-8 shadow-sm backdrop-blur-sm transition-all duration-300 hover:shadow-xl">
				<div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 opacity-0 transition-all duration-300 group-hover:opacity-100"></div>

				<div className="relative z-10 flex h-full flex-col">
					<div className="flex-1">
						<div className="flex flex-col items-center md:flex-row md:items-center md:gap-8">
							<div className="relative h-40 w-40 flex-shrink-0">
								<div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 opacity-20 blur-2xl transition-opacity group-hover:opacity-30"></div>
								<div className="absolute -inset-0.5 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 opacity-50 transition-all duration-300 group-hover:opacity-100"></div>
								{leader?.image && leader.image.url && (
									<Image
										src={leader.image.url}
										alt={leader.name || 'Leader image'}
										fill
										className="rounded-full border-4 border-white object-cover shadow-xl transition-transform duration-300 group-hover:scale-105"
									/>
								)}
							</div>

							<div className="mt-6 w-full md:mt-0">
								<h3 className="text-center text-2xl font-bold text-gray-900 md:text-left md:text-3xl">
									{leader.name}
								</h3>
								<p className="mt-1 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-center text-lg font-semibold text-transparent md:text-left md:text-xl">
									{leader.role}
								</p>
							</div>
						</div>

						<div className="mt-6 min-h-[80px]">
							<div className="flex flex-wrap justify-center gap-2 md:justify-start">
								{leader.badges &&
									leader.badges.map((badge, ind) => (
										<span
											key={ind}
											className="inline-block rounded-full bg-indigo-50 px-4 py-1.5 text-sm font-medium text-indigo-600">
											{badge.badge}
										</span>
									))}
							</div>
						</div>

						<div className="mt-4">
							<p className="text-center leading-relaxed text-gray-600 md:text-left">
								{leader.description}
							</p>
						</div>
					</div>

					<div className="mt-6 flex items-start">
						<a
							href={leader.linkedin_url}
							className="inline-flex items-center justify-center rounded-lg bg-gray-50 p-3 text-gray-600 transition-all duration-300 hover:bg-gray-100 hover:shadow-md">
							<svg
								className="h-5 w-5"
								fill="currentColor"
								viewBox="0 0 24 24">
								<path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
							</svg>
						</a>
					</div>
				</div>
			</div>
		</motion.div>
	);
};
